import React from 'react';
import {
  AnimateLayoutChanges,
  defaultAnimateLayoutChanges,
  useSortable,
} from '@dnd-kit/sortable';
import {
  Chip,
  DragIndicatorIcon,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@cherre-frontend/ui';

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

const buildWrapperSxStyle = (
  isDragging: boolean,
  transform: { x: number; y: number } | null,
  transition: string | undefined
): SxProps<Theme> => {
  return ({ palette }) => ({
    transition,
    gap: '10px',
    flexGrow: 1,
    padding: '4px 10px',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: '10px',
    border: `1px solid ${isDragging ? palette.primary.main : '#9E9E9E'}`,
    zIndex: isDragging ? 1000 : undefined,
    backgroundColor: isDragging ? '#FCE9F1' : undefined,
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
  });
};

export interface CustomAttributeCellProps {
  slug: string;
  label: string;
  type: string;
  required: boolean;
  disableReorder?: boolean;
  isTargetPropertyType: boolean;
}

export const CustomAttributeCell = ({
  slug,
  label,
  type,
  required,
  disableReorder,
  isTargetPropertyType,
}: CustomAttributeCellProps) => {
  const chipLabel = isTargetPropertyType ? 'Target' : 'Source';
  const chipColor = isTargetPropertyType ? '#9F4182' : '#FF6294';

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id: slug, animateLayoutChanges, disabled: disableReorder });

  return (
    <Stack
      ref={setNodeRef}
      {...attributes}
      sx={buildWrapperSxStyle(isDragging, transform, transition)}
    >
      <DragIndicatorIcon
        focusable
        component='svg'
        {...listeners}
        color={isDragging ? 'primary' : 'action'}
        ref={setActivatorNodeRef as (val: SVGElement | null) => void}
        cursor={
          disableReorder ? 'not-allowed' : isDragging ? 'grabbing' : 'grab'
        }
      />

      <Typography
        variant='subtitle2'
        color='#212121'
        flexGrow={1}
        fontWeight={600}
      >
        {label}
        {required && (
          <Typography
            color='primary'
            component='span'
            fontSize='16px'
            paddingLeft={0.5}
          >
            *
          </Typography>
        )}
      </Typography>

      <Chip
        size='small'
        label={<Typography color={chipColor}>{chipLabel}</Typography>}
      />

      <Stack
        minWidth={100}
        alignItems='center'
        paddingX={2}
        fontWeight={600}
        border='1px solid'
        borderRadius='6px'
        color={(theme) => theme.palette.primary.main}
        borderColor={(theme) => theme.palette.primary.main}
      >
        {type}
      </Stack>
    </Stack>
  );
};
